/**
 * THE PER-FIRM SWITCHES (decision 033, spec
 * docs/specs/bms/2026-10-22-002-cova-crm-per-firm.md §1 and §2.3).
 *
 * Cova staff turn Cova CRM on firm by firm, and inside it Workflows and Insight
 * sync. Each switch is a row in `insurance_broker_bms.firm_switches`; no row means off, so
 * every firm starts with everything off. A finer switch is ON exactly when its
 * own row and Cova CRM's row are both on: a finer row stored while Cova CRM is
 * off is inert, and comes back into force when Cova CRM does.
 *
 * BROWSER-SAFE AND PURE. The platform's reader (`FirmSwitchesService`) and the
 * identity carry the keys as opaque strings; their meaning lives here, read by
 * the gate on every lane, the firm's navigation and the super admin page. The
 * same rule is stated once in SQL, in `insurance_broker_bms.firm_switch_on` (v6.37/001),
 * and both are held to one list of cases (`testing/firm-switch-cases.ts`).
 *
 * THE KEYS ARE VENDOR-NEUTRAL (`sync`, never the vendor's name): the SQL
 * predicate lands in the sync group's engine statements, where the neutral
 * names ratchet bans it. The vendor's name is a label on screen only.
 *
 * A NEW SWITCH is one changeset that widens `chk_firm_switches_key`, one entry
 * below (the unit suite holds the keys to the CHECK), and its route or command
 * rows. The super admin page draws one column per entry, so it needs no edit.
 */

/** Every switch, in the order the CHECK lists them and the page draws them. */
export const FIRM_SWITCH_KEYS = ["crm", "workflows", "sync"] as const
export type FirmSwitchKey = (typeof FIRM_SWITCH_KEYS)[number]

/** One switch as the page shows it. `needs` names the switch it means nothing without. */
export interface FirmSwitchEntry {
  readonly key: FirmSwitchKey
  readonly label: string
  readonly description: string
  readonly needs: "crm" | null
}

export const FIRM_SWITCHES: ReadonlyArray<FirmSwitchEntry> = [
  {
    key: "crm",
    label: "Cova CRM",
    description: "Whether the firm sees Cova CRM at all.",
    needs: null,
  },
  {
    key: "workflows",
    label: "Workflows",
    description: "Whether the firm can start workflows and edit their templates.",
    needs: "crm",
  },
  {
    key: "sync",
    label: "Steadfast Apps Sync",
    description: "Whether the firm sees Steadfast Apps Sync, and whether its sync runs.",
    needs: "crm",
  },
]

const KNOWN: ReadonlySet<string> = new Set(FIRM_SWITCH_KEYS)

/** Whether `key` is one this build knows. */
export const isFirmSwitchKey = (key: string): key is FirmSwitchKey => KNOWN.has(key)

/**
 * THE §1 RULE: whether `key` is on for a firm whose rows on are `on`. Cova CRM
 * is on when its row is; a finer switch is on only while Cova CRM is on too. A
 * key this build does not know is off, so a row written by a newer build opens
 * nothing here.
 */
export const isSwitchOn = (on: ReadonlyArray<string>, key: string): boolean => {
  if (!isFirmSwitchKey(key) || !on.includes(key)) return false
  return key === "crm" || on.includes("crm")
}

// ============================================================================
// WHAT EACH REQUEST NEEDS (spec §3.2): two pure tables, so no lane decides for
// itself. Both always need Cova CRM, and add a finer switch by route or by
// command.
// ============================================================================

/**
 * The firm-facing sync routes, on both lanes: the connection, its conflicts
 * and its sends, the sync views (the record markers and the conflict
 * preview's live read), and the public lane's two sync leaves. Whole-segment
 * prefixes, so the staff lanes (`/api/bms/sync-admin-views`) never match.
 */
const SYNC_ROUTE_PREFIXES: ReadonlyArray<string> = [
  "/api/bms/sync/connection",
  "/api/bms/sync/conflicts",
  "/api/bms/sync/send",
  "/api/bms/sync-views",
  "/api/v1/sync-send",
  "/api/v1/sync-conflicts",
]

const isUnder = (path: string, prefix: string): boolean => path === prefix || path.startsWith(`${prefix}/`)

/**
 * The firm's own automation (§3.6): a person's start, and every write to a
 * template, the forecast included, which writes nothing but serves only the
 * template editor. Template reads stay open, because a live run's panel reads
 * its template's steps, and a run's own verbs and the boards are never gated.
 */
const isWorkflowWrite = (method: string, template: string): boolean =>
  (method === "POST" && template === "/api/bms/workflow/runs") ||
  (method !== "GET" && isUnder(template, "/api/bms/workflow/templates"))

/**
 * The switches a BMS request needs, by its method and its route template
 * (`/api/bms/clients/:id`), the same on the session lane and the public lane.
 * Every template needs Cova CRM, a template this table has never seen
 * included, so a new route is gated the day it ships; a sync route needs
 * Insight sync too, and a workflow start or template write Workflows. A
 * concrete path matches as its template does, which is what the read door
 * relies on.
 */
export const switchesForRoute = (method: string, template: string): ReadonlyArray<FirmSwitchKey> => {
  if (SYNC_ROUTE_PREFIXES.some((prefix) => isUnder(template, prefix))) return ["crm", "sync"]
  if (isWorkflowWrite(method.toUpperCase(), template)) return ["crm", "workflows"]
  return ["crm"]
}

/** The `cova` commands that are a workflow start or a template write, the rows `isWorkflowWrite` pins. */
const WORKFLOW_COMMANDS: ReadonlySet<string> = new Set([
  "workflow-runs create",
  "workflow-templates create",
  "workflow-templates edit",
  "workflow-templates duplicate",
  "workflow-templates archive",
  "workflow-templates restore",
  "workflow-templates delete",
  "workflow-templates forecast",
  "workflow-templates starts",
])

/**
 * The switches a `cova` book command needs, by its noun and verb, so the CLI
 * lane and the HTTP lanes agree. Every command needs Cova CRM; every verb of
 * a `sync-*` noun (all of them firm-facing) needs Insight sync too, and a
 * workflow start or template write Workflows.
 */
export const switchesForCommand = (noun: string, verb: string): ReadonlyArray<FirmSwitchKey> => {
  if (noun.startsWith("sync-")) return ["crm", "sync"]
  if (WORKFLOW_COMMANDS.has(`${noun} ${verb}`)) return ["crm", "workflows"]
  return ["crm"]
}

/**
 * THE ONE MESSAGE every refusal carries, on every lane, which names no switch
 * (the calls rollout's anti-oracle): a firm learns only that this part of Cova
 * is not on for it. The firm's own screens never show it; they hide the door.
 */
export const FIRM_SWITCH_REFUSAL = "This part of Cova is not switched on for your firm."

/** Whether every switch in `required` is on for a firm whose rows on are `on`. */
export const admitsFirmSwitches = (on: ReadonlyArray<string>, required: ReadonlyArray<string>): boolean =>
  required.every((key) => isSwitchOn(on, key))
